import { Navigate, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import type { ReactNode } from 'react'
import { orgAppClient } from '../services/orgAppClient'
import { useOrgAuth } from '../contexts/OrgAuthContext'

// ── Landing-local design language ────────────────────────────────────────────
// One primary (brand green, from the logo), near-black ink, warm paper canvas.
// CTA pills are landing-only styles; ui/Button stays untouched for the app.

const DEMO_MAILTO = 'mailto:contact@rochd.systems?subject=Démonstration'

const PRIMARY_CTA =
  'inline-flex items-center justify-center rounded-lg bg-brand-600 px-7 h-12 text-base font-medium text-white transition-colors hover:bg-brand-700'
const GHOST_CTA =
  'inline-flex items-center justify-center rounded-lg bg-white border border-slate-300 px-7 h-12 text-base font-medium text-ink transition-colors hover:border-slate-400 hover:bg-slate-50'

const H2 =
  'text-3xl sm:text-4xl font-semibold tracking-tight text-ink max-w-2xl'

function Icon({ d, circle }: { d: string; circle?: boolean }) {
  return (
    <svg
      className="h-5 w-5"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {circle && <circle cx="12" cy="12" r="9" />}
      <path d={d} />
    </svg>
  )
}

const ICON_PATHS = {
  procurements: 'M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14ZM20 20l-3.5-3.5',
  ai: 'M5 8h14a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2ZM12 8V5M9 13h.01M15 13h.01M9 17h6',
  website: 'M3 12h18M12 3a15 15 0 0 1 0 18M12 3a15 15 0 0 0 0 18',
  documents: 'M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5ZM14 3v5h5M9 13h6M9 17h4',
  tools: 'M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z',
  projects: 'M9 4a3 3 0 0 1 6 0M5 6h14a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1ZM9 11h6M9 15h4',
  org: 'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 3a4 4 0 1 1 0 8 4 4 0 0 1 0-8ZM22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75',
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
    </svg>
  )
}

function ProofItem({ children, dark = false }: { children: ReactNode; dark?: boolean }) {
  return (
    <li className="flex items-start gap-3">
      <span
        className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${
          dark ? 'bg-white/10 text-brand-200' : 'bg-brand-100 text-brand-700'
        }`}
      >
        <CheckIcon className="h-3 w-3" />
      </span>
      <span className={`text-base leading-relaxed ${dark ? 'text-slate-200' : 'text-slate-700'}`}>{children}</span>
    </li>
  )
}

// ── Mock product visuals (hand-built, no screenshots, aria-hidden) ───────────

function FiltersMock() {
  const { t } = useTranslation()
  const chips = t('home.mock.filterChips', { returnObjects: true, defaultValue: [] }) as string[]
  const tenders = t('home.mock.tenders', {
    returnObjects: true,
    defaultValue: [],
  }) as { title: string; amount: string; status: string; closing?: boolean }[]
  return (
    <div className="w-full max-w-md mx-auto" aria-hidden="true">
      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex items-center gap-2 rounded-full bg-slate-100 px-4 py-2.5 text-sm text-slate-500">
          <Icon d={ICON_PATHS.procurements} />
          {t('home.mock.search')}
        </div>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {(Array.isArray(chips) ? chips : []).map((chip) => (
            <span key={chip} className="rounded-full border border-brand-200 bg-brand-50 px-2.5 py-1 text-xs font-medium text-brand-700">
              {chip}
            </span>
          ))}
        </div>
        <p className="mt-3 text-xs font-medium text-slate-500">{t('home.mock.resultsCount')}</p>
        <div className="mt-1 divide-y divide-slate-100">
          {(Array.isArray(tenders) ? tenders : []).map((tender) => (
            <div key={tender.title} className="flex items-center justify-between gap-3 py-3">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-ink">{tender.title}</p>
                <p className="text-xs text-slate-500">{tender.amount}</p>
              </div>
              <span
                className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${
                  tender.closing ? 'bg-amber-50 text-amber-700' : 'bg-brand-50 text-brand-700'
                }`}
              >
                {tender.status}
              </span>
            </div>
          ))}
        </div>
      </div>
      <div className="mt-3 flex items-center gap-2 rounded-xl border border-brand-200 bg-white/80 px-4 py-3 text-xs text-brand-800">
        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand-100 text-brand-700">
          <CheckIcon className="h-3 w-3" />
        </span>
        {t('home.mock.audit')}
      </div>
    </div>
  )
}

function DiffMock() {
  const { t } = useTranslation()
  const docChips = t('home.mock.docChips', { returnObjects: true, defaultValue: [] }) as string[]
  return (
    <div className="w-full max-w-md mx-auto" aria-hidden="true">
      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-center gap-1.5">
          {(Array.isArray(docChips) ? docChips : []).map((chip, i) => (
            <span
              key={chip}
              className={`rounded-full px-2.5 py-1 text-xs font-medium ${
                i === 0 ? 'bg-brand-600 text-white' : 'bg-slate-100 text-slate-600'
              }`}
            >
              {chip}
            </span>
          ))}
        </div>
        <p className="mt-3 text-sm font-medium text-ink">{t('home.mock.docName')}</p>
        <div className="mt-3 space-y-2.5">
          <div className="h-2 w-4/5 rounded-full bg-slate-100" />
          <div className="h-2 w-3/5 rounded-full bg-slate-100" />
          <p className="rounded-sm bg-red-50 px-2 py-1 text-sm text-slate-500 line-through decoration-red-300">
            {t('home.mock.lineOld')}
          </p>
          <p className="rounded-sm bg-brand-50 px-2 py-1 text-sm font-medium text-brand-800 underline decoration-brand-400">
            {t('home.mock.lineNew')}
          </p>
          <div className="h-2 w-2/3 rounded-full bg-slate-100" />
        </div>
      </div>
      <div className="mt-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <p className="inline-block rounded-2xl rounded-ss-none bg-slate-100 px-4 py-2.5 text-sm text-slate-700">
          {t('home.mock.chatBubble')}
        </p>
      </div>
    </div>
  )
}

function AiGovernanceMock() {
  const { t } = useTranslation()
  const groups = t('home.mock.groups', { returnObjects: true, defaultValue: [] }) as { name: string; cap: string }[]
  return (
    <div className="w-full max-w-md mx-auto space-y-3" aria-hidden="true">
      <div className="rounded-2xl bg-white p-5 shadow-sm">
        <div className="flex items-center justify-between">
          <p className="text-xs font-medium text-slate-500">{t('home.mock.walletLabel')}</p>
          <span className="rounded-lg bg-brand-600 px-3 py-1.5 text-xs font-medium text-white">{t('home.mock.topUp')}</span>
        </div>
        <p className="mt-2 text-2xl font-semibold tracking-tight text-ink">{t('home.mock.balance')}</p>
      </div>
      <div className="rounded-2xl bg-white p-4 shadow-sm">
        <div className="divide-y divide-slate-100">
          {(Array.isArray(groups) ? groups : []).map((group) => (
            <div key={group.name} className="flex items-center justify-between gap-3 py-3 first:pt-0 last:pb-0">
              <p className="text-sm font-medium text-ink">{group.name}</p>
              <div className="flex items-center gap-2.5">
                <span className="flex gap-1">
                  <span className="h-1.5 w-1.5 rounded-full bg-brand-600" />
                  <span className="h-1.5 w-1.5 rounded-full bg-brand-300" />
                  <span className="h-1.5 w-1.5 rounded-full bg-slate-200" />
                </span>
                <span className="text-xs text-slate-500">{group.cap}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="rounded-2xl bg-white p-4 shadow-sm">
        <div className="flex items-center justify-between text-xs">
          <span className="font-medium text-slate-500">{t('home.mock.consumptionLabel')}</span>
          <span className="font-semibold text-ink">{t('home.mock.consumptionValue')}</span>
        </div>
        <div className="mt-2 h-2 rounded-full bg-slate-100">
          <div className="h-2 w-1/4 rounded-full bg-brand-500" />
        </div>
      </div>
    </div>
  )
}

function WebsiteMock() {
  const { t } = useTranslation()
  return (
    <div className="w-full max-w-md mx-auto" aria-hidden="true">
      <div className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-3 shadow-sm">
        <p className="flex-1 truncate text-sm text-slate-600">{t('home.mock.prompt')}</p>
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-600 text-white">
          <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 19V5M5 12l7-7 7 7" />
          </svg>
        </span>
      </div>
      <div className="mt-3 rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <div className="flex items-center gap-2 border-b border-slate-100 px-4 py-2.5">
          <span className="h-2 w-2 rounded-full bg-slate-200" />
          <span className="h-2 w-2 rounded-full bg-slate-200" />
          <span className="h-2 w-2 rounded-full bg-slate-200" />
          <span dir="ltr" className="ms-2 flex items-center gap-1 rounded-md bg-slate-100 px-2 py-0.5 text-xs text-slate-500">
            <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
              <rect x="5" y="11" width="14" height="9" rx="2" />
              <path d="M8 11V8a4 4 0 0 1 8 0v3" />
            </svg>
            {t('home.mock.siteUrl')}
          </span>
        </div>
        <div className="px-6 py-7 text-center">
          <p className="text-lg font-semibold text-ink">{t('home.mock.siteHeadline')}</p>
          <p className="mt-1 text-xs text-slate-500">{t('home.mock.siteSub')}</p>
          <span className="mt-3 inline-block rounded-lg bg-brand-600 px-3 py-1.5 text-xs font-medium text-white">
            {t('home.mock.siteCta')}
          </span>
          <div className="mt-5 grid grid-cols-2 gap-2">
            <div className="h-12 rounded-lg bg-brand-50" />
            <div className="h-12 rounded-lg bg-slate-100" />
          </div>
        </div>
      </div>
    </div>
  )
}

function ProofList({ items, dark = false }: { items: string[]; dark?: boolean }) {
  return (
    <ul className="mt-8 space-y-4">
      {(Array.isArray(items) ? items : []).map((item) => (
        <ProofItem key={item} dark={dark}>
          {item}
        </ProofItem>
      ))}
    </ul>
  )
}

// ── Page ─────────────────────────────────────────────────────────────────────

export default function HomePage() {
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()
  const { session, isLoading } = useOrgAuth()

  if (!isLoading && session) {
    return <Navigate to="/app" replace />
  }

  const handleModuleAccess = async (path: string) => {
    try {
      await orgAppClient.getSession()
      navigate(path)
    } catch {
      navigate(`/login?redirect=${encodeURIComponent(path)}`)
    }
  }

  const isRtl = i18n.dir() === 'rtl'
  const accentClass = isRtl ? 'text-brand-700' : 'font-serif italic text-brand-700'

  const modules = [
    { key: 'procurements', icon: ICON_PATHS.procurements },
    { key: 'ai', icon: ICON_PATHS.ai },
    { key: 'website', icon: ICON_PATHS.website },
    { key: 'documents', icon: ICON_PATHS.documents },
    { key: 'tools', icon: ICON_PATHS.tools },
    { key: 'projects', icon: ICON_PATHS.projects },
    { key: 'org', icon: ICON_PATHS.org },
  ].map((m) => ({
    ...m,
    title: t(`home.modules.${m.key}.title`, m.key),
    tagline: t(`home.modules.${m.key}.tagline`, ''),
    link: t(`home.modules.${m.key}.link`, ''),
  }))

  const faqs = t('home.faq.items', { returnObjects: true, defaultValue: [] }) as { q: string; a: string }[]
  const filterItems = t('home.proof.filters.items', { returnObjects: true, defaultValue: [] }) as string[]
  const documentItems = t('home.proof.documents.items', { returnObjects: true, defaultValue: [] }) as string[]
  const aiItems = t('home.proof.ai.items', { returnObjects: true, defaultValue: [] }) as string[]

  const demoAndLogin = (
    <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
      <a href={DEMO_MAILTO} className={PRIMARY_CTA}>
        {t('home.contactDemo', 'Request a demo')}
      </a>
      <button type="button" onClick={() => handleModuleAccess('/app')} className={GHOST_CTA}>
        {t('home.login', 'Log in')}
      </button>
    </div>
  )

  return (
    <div className="bg-paper">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-brand-50 via-paper to-paper" aria-hidden="true" />
        <div className="relative mx-auto max-w-6xl px-4 sm:px-6 pt-20 sm:pt-28 pb-16 sm:pb-20 text-center">
          <h1 className="mx-auto max-w-3xl text-4xl sm:text-6xl font-semibold tracking-tight leading-[1.08] text-ink">
            {t('home.hero.title', 'One platform to run your company,')}{' '}
            <span className={accentClass}>{t('home.hero.title_accent', 'built for Morocco.')}</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-slate-600 leading-relaxed">
            {t('home.hero.subtitle', 'Tenders, documents, AI, and your website, together in one place, in French and in Arabic.')}
          </p>
          {demoAndLogin}
        </div>
      </section>

      <div className="mx-auto max-w-6xl space-y-20 sm:space-y-28 px-4 sm:px-6 py-16 sm:py-24">
        {/* Modules */}
        <section>
          <h2 className={H2}>{t('home.modules.heading', "What's inside Rochd")}</h2>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {modules.map((module) => (
              <button
                key={module.key}
                type="button"
                onClick={() => handleModuleAccess('/app')}
                className="group flex flex-col items-start rounded-2xl border border-slate-200 bg-white p-6 text-start transition-shadow hover:shadow-sm"
              >
                <span className="mb-5 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-brand-50 text-brand-700">
                  <Icon d={module.icon} />
                </span>
                <h3 className="text-base font-semibold text-ink">{module.title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{module.tagline}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-brand-700">
                  {module.link}
                  <svg
                    className="h-4 w-4 rtl:rotate-180 transition-transform group-hover:translate-x-0.5 group-hover:rtl:-translate-x-0.5"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="m9 18 6-6-6-6" />
                  </svg>
                </span>
              </button>
            ))}
          </div>
        </section>

        {/* Proof 1: finding tenders */}
        <section className="rounded-3xl bg-brand-50 px-6 py-14 sm:px-12 sm:py-20">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div>
              <h2 className={H2}>{t('home.proof.filters.heading', 'Stop guessing which tenders match.')}</h2>
              <ProofList items={filterItems} />
            </div>
            <FiltersMock />
          </div>
        </section>

        {/* Proof 2: understanding tenders */}
        <section className="rounded-3xl bg-slate-100 px-6 py-14 sm:px-12 sm:py-20">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div className="lg:order-last">
              <h2 className={H2}>{t('home.proof.documents.heading', 'Understand every tender, down to the last change.')}</h2>
              <ProofList items={documentItems} />
            </div>
            <div className="lg:order-first">
              <DiffMock />
            </div>
          </div>
        </section>

        {/* Proof 3: Rochd AI (the one dark moment) */}
        <section className="rounded-3xl bg-ink px-6 py-14 sm:px-12 sm:py-20">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div>
              <h2 className={`${H2} text-white`}>{t('home.proof.ai.heading', 'One AI budget for the whole company.')}</h2>
              <ProofList items={aiItems} dark />
            </div>
            <AiGovernanceMock />
          </div>
        </section>

        {/* Proof 4: website builder */}
        <section className="rounded-3xl bg-brand-50 px-6 py-14 sm:px-12 sm:py-20">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div>
              <h2 className={H2}>{t('home.proof.website.heading', 'AI writes the first draft. You keep the pen.')}</h2>
              <p className="mt-6 text-base leading-relaxed text-slate-700">
                {t('home.proof.website.body', '')}
              </p>
            </div>
            <WebsiteMock />
          </div>
        </section>

        {/* Coming soon */}
        <section>
          <h2 className={H2}>{t('home.modules.coming_soon', 'Coming soon')}</h2>
          <div className="mt-8 grid gap-8 sm:grid-cols-2 sm:gap-0 sm:divide-x sm:divide-slate-200 rtl:sm:divide-x-reverse">
            {(['hr', 'invoicing'] as const).map((key) => (
              <div key={key} className="flex items-start gap-3 sm:px-8 sm:first:ps-0 sm:last:pe-0">
                <svg
                  className="mt-0.5 h-5 w-5 shrink-0 text-slate-400"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.8}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <circle cx="12" cy="12" r="9" />
                  <path d="M12 8v4l3 3" />
                </svg>
                <div>
                  <h3 className="text-base font-semibold text-ink">{t(`home.modules.${key}.title`)}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-slate-600">{t(`home.modules.${key}.description`)}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section>
          <h2 className={H2}>{t('home.faq.heading', 'Frequently asked questions')}</h2>
          <div className="mt-8 max-w-3xl border-t border-slate-200">
            {(Array.isArray(faqs) ? faqs : []).map((faq) => (
              <details key={faq.q} className="group border-b border-slate-200 py-5">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-start font-medium text-ink">
                  {faq.q}
                  <svg
                    className="h-5 w-5 shrink-0 text-slate-400 transition-transform group-open:rotate-45"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    strokeLinecap="round"
                    aria-hidden="true"
                  >
                    <path d="M12 5v14M5 12h14" />
                  </svg>
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-slate-600">{faq.a}</p>
              </details>
            ))}
          </div>
        </section>
      </div>

      {/* Final CTA */}
      <section className="relative">
        <div className="absolute inset-0 bg-gradient-to-b from-brand-50 to-transparent" aria-hidden="true" />
        <div className="relative mx-auto max-w-3xl px-4 sm:px-6 pt-16 sm:pt-24 pb-24 sm:pb-32 text-center">
          <h2 className="text-3xl sm:text-5xl font-semibold tracking-tight text-ink">
            {t('home.cta.heading', 'See it in action.')}
          </h2>
          {demoAndLogin}
        </div>
      </section>
    </div>
  )
}
